"use client";

import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FeatherMenu, FeatherX } from "@subframe/core";
import { FeatherHome } from "@subframe/core";
import { FeatherZap } from "@subframe/core";
import { FeatherLogOut } from "@subframe/core";
import { FeatherLogIn } from "@subframe/core";
import { FeatherSettings } from "@subframe/core";
import { FeatherWallet } from "@subframe/core";
import { FeatherBell } from "@subframe/core";
import { FeatherGrid } from "@subframe/core";
import { FeatherAward } from "@subframe/core";
import { FeatherLayers } from "@subframe/core";
import { FeatherUsers } from "@subframe/core";
import { FeatherMessageCircle } from "@subframe/core";
import { FeatherRefreshCw } from "@subframe/core";
import { FeatherArrowLeftRight } from "@subframe/core";
import { FeatherEdit2 } from "@subframe/core";
import { FeatherLayout } from "@subframe/core";
import { FeatherSend } from "@subframe/core";
import { FeatherDroplet } from "@subframe/core";
import { FeatherStar } from "@subframe/core";
import { useAuthStore } from "../store/authStore";
import { useNotificationStore } from "../store/notificationStore";
import { useUnreadCount } from "../hooks/useUnreadCount";
import { useUnreadMessageCount } from "../hooks/useMessages";
import { useNewOfferCount } from "../hooks/useNewOfferCount";
import { usePendingTradeCount } from "../hooks/usePendingTradeCount";
import { useSignInModalStore } from "../store/signInModalStore";
import { logout as apiLogout } from "../api/auth";
import { formatAddress } from "../utils/formatters";
import { AnimatedStar } from "./ui/AnimatedStar";

interface MobileNavProps {
  className?: string;
}

export const MobileNav: React.FC<MobileNavProps> = ({ className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuthStore();
  const { success: showSuccess } = useNotificationStore();
  const { unreadCount } = useUnreadCount({ enablePolling: isAuthenticated });
  const { unreadCount: unreadMessageCount } = useUnreadMessageCount({ enablePolling: isAuthenticated });
  const { newOfferCount } = useNewOfferCount({ enablePolling: isAuthenticated });
  const { count: pendingTradeCount } = usePendingTradeCount({ enablePolling: isAuthenticated });
  const openSignIn = useSignInModalStore((st) => st.open);
  const navigate = useNavigate();
  const location = useLocation();
  const displayAddress = user?.xrpl_address
    ? formatAddress(user.xrpl_address)
    : "Not connected";

  const handleLogout = async () => {
    try {
      await apiLogout();
      logout();
      showSuccess("Successfully logged out");
      navigate("/");
    } catch (err) {
      logout();
      showSuccess("Logged out");
      navigate("/");
    }
    setIsOpen(false);
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;

  // Main nav items (matching sidebar structure)
  const mainNavItems = [
    { path: "/", label: "Home", icon: <FeatherHome /> },
    { path: "/deals", label: "Deals", icon: <FeatherZap /> },
    { path: "/projects", label: "Projects", icon: <FeatherLayers /> },
    { path: "/collections", label: "Collections", icon: <FeatherGrid /> },
    { path: "/drops", label: "Drops", icon: <FeatherDroplet /> },
    { path: "/flip", label: "Flip NFTs", icon: <FeatherRefreshCw /> },
    { path: "/leaderboard", label: "Leaderboard", icon: <FeatherAward /> },
    { path: "/ambassadors", label: "Ambassadors", icon: <AnimatedStar className="h-5 w-5" /> },
  ];

  const navItems = [...mainNavItems];

  // Creator section items
  const creatorNavItems = isAuthenticated
    ? [
        { path: "/creator", label: "Dashboard", icon: <FeatherLayout /> },
        { path: "/launchpad", label: "Launchpad", icon: <FeatherSend /> },
      ]
    : [];

  // Authenticated nav items under "My Account" section
  const authenticatedNavItems = isAuthenticated
    ? [
        {
          path: `/wallets/${user?.xrpl_address}${newOfferCount > 0 ? '?tab=offers' : ''}`,
          label: "My Wallet",
          icon: <FeatherWallet />,
        },
        { path: "/inbox", label: "Messages", icon: <FeatherMessageCircle /> },
        { path: "/notifications", label: "Notifications", icon: <FeatherBell /> },
        { path: "/trades", label: "Trades", icon: <FeatherArrowLeftRight /> },
        { path: "/referrals", label: "Referrals", icon: <FeatherUsers /> },
        { path: "/settings", label: "Settings", icon: <FeatherSettings /> },
      ]
    : [];

  return (
    <div className={`hidden mobile:block ${className || ""}`}>
      {/* Hamburger Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center justify-center w-10 h-10 rounded-md bg-neutral-50 hover:bg-neutral-100 active:bg-neutral-200 transition-colors"
        aria-label="Open navigation menu"
      >
        <FeatherMenu className="w-5 h-5 text-default-font" />
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Slide-out Menu */}
      <div
        className={`fixed top-0 left-0 h-full w-[280px] bg-default-background border-r border-solid border-neutral-border z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-solid border-neutral-border">
          <Link to="/" onClick={() => setIsOpen(false)}>
            <img
              className="h-8 flex-none object-contain"
              src="/logo-transparent.png"
              alt="XRP Deals"
            />
          </Link>
          <button
            onClick={() => setIsOpen(false)}
            className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-neutral-50 transition-colors"
            aria-label="Close navigation menu"
          >
            <FeatherX className="w-5 h-5 text-default-font" />
          </button>
        </div>

        {/* Navigation Items */}
        <div className="flex flex-col py-4 overflow-y-auto h-[calc(100%-140px)]">
          {/* Main Navigation */}
          <div className="flex flex-col px-3 gap-1">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavClick(item.path)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-left w-full transition-colors ${
                  isActive(item.path)
                    ? "bg-brand-50 text-brand-700"
                    : "text-neutral-500 hover:bg-neutral-50"
                }`}
              >
                <span className="w-5 h-5">{item.icon}</span>
                <span className="text-body-bold font-body-bold">
                  {item.label}
                </span>
              </button>
            ))}
          </div>

          {isAuthenticated && (
            <>
              <div className="px-4 py-3 mt-4">
                <span className="text-caption-bold font-caption-bold text-subtext-color uppercase tracking-wider">
                  Creator
                </span>
              </div>
              <div className="flex flex-col px-3 gap-1">
                {creatorNavItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleNavClick(item.path)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-left w-full transition-colors ${
                      isActive(item.path) || (item.path === '/creator' && location.pathname.startsWith('/creator')) || (item.path === '/launchpad' && location.pathname.startsWith('/launchpad'))
                        ? "bg-brand-50 text-brand-700"
                        : "text-neutral-500 hover:bg-neutral-50"
                    }`}
                  >
                    <span className="w-5 h-5">{item.icon}</span>
                    <span className="text-body-bold font-body-bold">
                      {item.label}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Authenticated Navigation */}
          {isAuthenticated ? (
            <>
              <div className="px-4 py-3 mt-4">
                <span className="text-caption-bold font-caption-bold text-subtext-color uppercase tracking-wider">
                  My Account
                </span>
              </div>
              <div className="flex flex-col px-3 gap-1">
                {authenticatedNavItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleNavClick(item.path)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-left w-full transition-colors ${
                      isActive(item.path)
                        ? "bg-brand-50 text-brand-700"
                        : "text-neutral-500 hover:bg-neutral-50"
                    }`}
                  >
                    <span className="w-5 h-5">{item.icon}</span>
                    <span className="text-body-bold font-body-bold">
                      {item.label}
                    </span>
                    {item.path === "/inbox" && unreadMessageCount > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold text-white bg-blue-500 rounded-full min-w-[1.25rem]">
                        {unreadMessageCount > 99 ? "99+" : unreadMessageCount}
                      </span>
                    )}
                    {item.path === "/notifications" && unreadCount > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium text-neutral-500 bg-default-background border border-neutral-800 rounded-full min-w-[1.25rem]">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                    {item.path === "/trades" && pendingTradeCount > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold text-white bg-brand-600 rounded-full min-w-[1.25rem]">
                        {pendingTradeCount > 99 ? "99+" : pendingTradeCount}
                      </span>
                    )}
                    {item.path.startsWith("/wallets/") && newOfferCount > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold text-white bg-amber-500 rounded-full min-w-[1.25rem]">
                        {newOfferCount > 99 ? "99+" : newOfferCount}
                      </span>
                    )}
                  </button>
                ))}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-md text-left w-full text-neutral-500 hover:bg-neutral-50 transition-colors"
                >
                  <span className="w-5 h-5">
                    <FeatherLogOut />
                  </span>
                  <span className="text-body-bold font-body-bold">Logout</span>
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col px-3 gap-1 mt-4">
              <button
                onClick={() => {
                  openSignIn();
                  setIsOpen(false);
                }}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md text-left w-full text-brand-700 bg-brand-50 hover:bg-brand-100 transition-colors"
              >
                <span className="w-5 h-5">
                  <FeatherLogIn />
                </span>
                <span className="text-body-bold font-body-bold">Sign In</span>
              </button>
            </div>
          )}
        </div>

        {/* Footer with Wallet Address */}
        {isAuthenticated && (
          <div className="absolute bottom-0 left-0 right-0 flex items-center gap-2 px-4 py-4 border-t border-solid border-neutral-border bg-default-background">
            <FeatherWallet className="text-body font-body text-subtext-color" />
            <span className="text-caption-bold font-caption-bold text-default-font">
              {displayAddress}
            </span>
          </div>
        )}
      </div>

    </div>
  );
};
